import {
  GradientBlurKeyframe,
  GradientBlurConfig,
  GradientDirection,
} from "../../types";

/**
 * Represents the calculated gradient blur state at a specific frame
 */
export interface GradientBlurState {
  activeGradients: GradientBlurKeyframe[];
  isActive: boolean;
}

/**
 * Calculates which gradient blur keyframes are active at the current frame
 *
 * @param frame - Current frame relative to overlay start (0 = first frame)
 * @param gradientConfig - The overlay's gradient blur configuration
 * @returns GradientBlurState with active gradients
 */
export function calculateGradientBlurState(
  frame: number,
  gradientConfig: GradientBlurConfig | undefined
): GradientBlurState {
  if (!gradientConfig?.enabled || !gradientConfig.keyframes?.length) {
    return { activeGradients: [], isActive: false };
  }

  // Find all keyframes active at current frame
  const activeGradients = gradientConfig.keyframes.filter(
    (kf) => frame >= kf.startFrame && frame <= kf.endFrame
  );

  return {
    activeGradients,
    isActive: activeGradients.length > 0,
  };
}

/**
 * Ease-out cubic easing function
 * Creates smooth deceleration - starts fast, ends slow
 * Industry standard for professional video editing
 *
 * @param t - Linear progress (0 to 1)
 * @returns Eased progress (0 to 1)
 */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Generates color stops with ease-out cubic easing for premium gradient appearance
 *
 * @param startPercent - Where gradient starts (0-100)
 * @param endPercent - Where gradient becomes fully transparent (0-100)
 * @param color - RGB object {r, g, b}
 * @param opacity - Overall opacity (0-100)
 * @param numStops - Number of color stops (default: 10 for high quality)
 * @returns Array of CSS color stop strings
 */
function generateEasedColorStops(
  startPercent: number,
  endPercent: number,
  color: { r: number; g: number; b: number },
  opacity: number,
  numStops: number = 10
): string[] {
  const stops: string[] = [];
  const range = endPercent - startPercent;

  for (let i = 0; i <= numStops; i++) {
    // Linear progress from 0 to 1
    const linearProgress = i / numStops;

    // Apply ease-out cubic: easing(t) = 1 - (1 - t)³
    const easedProgress = easeOutCubic(linearProgress);

    // Calculate position percentage
    const position = startPercent + range * linearProgress;

    // Calculate opacity with easing (fades out following ease-out curve)
    const currentOpacity = (opacity / 100) * (1 - easedProgress);

    // Generate CSS color stop
    const stopColor = `rgba(${color.r}, ${color.g}, ${color.b}, ${currentOpacity.toFixed(3)})`;
    stops.push(`${stopColor} ${position.toFixed(1)}%`);
  }

  return stops;
}

/**
 * Generates CSS linear-gradient string for a single gradient with premium easing
 *
 * @param gradient - The gradient blur keyframe
 * @returns CSS linear-gradient string with eased color stops
 */
export function generateGradientCSS(gradient: GradientBlurKeyframe): string {
  const { direction, color, startPercent, endPercent, opacity } = gradient;

  // Convert hex color to RGB object
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Determine gradient direction
  const directionMap: Record<GradientDirection, string> = {
    top: "to bottom",
    bottom: "to top",
    left: "to right",
    right: "to left",
  };

  // Generate eased color stops (10 stops for high quality, smooth premium look)
  const colorStops = generateEasedColorStops(
    startPercent,
    endPercent,
    { r, g, b },
    opacity,
    10 // High quality: 10 color stops for imperceptible banding
  );

  // Join color stops with commas to create smooth, premium gradient
  return `linear-gradient(${directionMap[direction]}, ${colorStops.join(", ")})`;
}

/**
 * Validates gradient blur keyframes
 *
 * @param keyframes - Array of gradient blur keyframes to validate
 * @returns Validation result with error message if invalid
 */
export function validateGradientKeyframes(
  keyframes: GradientBlurKeyframe[]
): {
  valid: boolean;
  error?: string;
} {
  if (!keyframes || keyframes.length === 0) {
    return { valid: true };
  }

  for (const kf of keyframes) {
    if (kf.startFrame >= kf.endFrame) {
      return {
        valid: false,
        error: `Invalid keyframe: start frame must be less than end frame`,
      };
    }
    if (kf.startPercent >= kf.endPercent) {
      return {
        valid: false,
        error: `Start percent (${kf.startPercent}) must be less than end percent (${kf.endPercent})`,
      };
    }
    if (kf.opacity < 0 || kf.opacity > 100) {
      return {
        valid: false,
        error: `Opacity must be between 0 and 100`,
      };
    }
    if (kf.startPercent < 0 || kf.startPercent > 100) {
      return {
        valid: false,
        error: `Start percent must be between 0 and 100`,
      };
    }
    if (kf.endPercent < 0 || kf.endPercent > 100) {
      return {
        valid: false,
        error: `End percent must be between 0 and 100`,
      };
    }
  }

  return { valid: true };
}

/**
 * Creates a new gradient blur keyframe with default values
 *
 * @param startFrame - Start frame for the gradient
 * @param endFrame - End frame for the gradient
 * @param direction - Gradient direction (default: 'bottom')
 * @returns New gradient blur keyframe with default values
 */
export function createGradientBlurKeyframe(
  startFrame: number,
  endFrame: number,
  direction: GradientDirection = "bottom"
): GradientBlurKeyframe {
  return {
    id: `gradient-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    startFrame,
    endFrame,
    direction,
    color: "#000000",
    startPercent: 0,
    endPercent: 40,
    opacity: 80,
  };
}
